import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { subjectList } from "../../../api/Subjects";
import { courseAdd } from "../../../api/Courses";

import { LoadingDiv, SweetAlertShow } from "../../master/Master";

import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import AcademicPortalLayout from "../AcademicPortalLayout";

export default function CourseAdd() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);

  const [subjectSearch, setSubjectSearch] = useState("");

  /* =========================================================
     GET SUBJECT LIST
  ========================================================= */

  const getSubjectList = async () => {
    setLoading(true);

    try {
      const response = await subjectList();

      if (response?.data?.status) {
        setSubjects(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     CREATE COURSE
  ========================================================= */

  const createCourse = async (e) => {
    e.preventDefault();

    if (!selectedSubjects.length) {
      SweetAlertShow(
        "info",
        "Subjects Required",
        "Please select at least one subject.",
      );

      return;
    }

    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await courseAdd(formData);

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);

        navigate("/courses");
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     SUBJECT SEARCH
  ========================================================= */

  const filteredSubjects = useMemo(() => {
    const searchValue = subjectSearch.trim().toLowerCase();

    if (!searchValue) {
      return subjects;
    }

    return subjects.filter((subject) =>
      subject?.name?.toLowerCase()?.includes(searchValue),
    );
  }, [subjects, subjectSearch]);

  /* =========================================================
     SUBJECT SELECTION
  ========================================================= */

  const toggleSubject = (subjectId) => {
    setSelectedSubjects((previous) => {
      if (previous.includes(subjectId)) {
        return previous.filter((id) => id !== subjectId);
      }

      return [...previous, subjectId];
    });
  };

  const selectAllSubjects = () => {
    setSelectedSubjects(subjects.map((subject) => subject.id));
  };

  const clearAllSubjects = () => {
    setSelectedSubjects([]);
  };

  /* =========================================================
     EFFECT
  ========================================================= */

  useEffect(() => {
    getSubjectList();
  }, []);

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <AcademicPortalLayout />

                  <div className="col-12">
                    {/* =================================================
                        MAIN CARD
                    ================================================= */}

                    <div className="card course-add-card">
                      {/* =================================================
                          HEADER
                      ================================================= */}

                      <div className="course-add-header">
                        <div className="d-flex align-items-start gap-3">
                          <div className="course-add-icon">
                            <i className="ri-book-line"></i>
                          </div>

                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <h4 className="mb-0 fw-semibold">
                                Add Course / Class
                              </h4>
                            </div>

                            <p className="text-muted mb-0 mt-1">
                              Create a new academic course and assign its
                              subjects.
                            </p>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => navigate("/courses")}
                        >
                          <i className="ri-arrow-left-line me-1"></i>
                          Back
                        </button>
                      </div>

                      {/* =================================================
                          FORM
                      ================================================= */}

                      <form onSubmit={createCourse}>
                        <div className="course-form-body">
                          {/* =================================================
                              BASIC INFORMATION
                          ================================================= */}

                          <div className="form-section">
                            <div className="form-section-heading">
                              <div className="form-section-number">1</div>

                              <div>
                                <h6 className="mb-0">Course Information</h6>

                                <small className="text-muted">
                                  Enter the basic details of the course or
                                  class.
                                </small>
                              </div>
                            </div>

                            <div className="row g-4 mt-1">
                              {/* Course Title */}

                              <div className="col-lg-6">
                                <label className="form-label">
                                  Course / Class Title
                                  <span className="text-danger ms-1">*</span>
                                </label>

                                <div className="course-input-wrapper">
                                  <i className="ri-book-2-line"></i>

                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Example: Class 10th"
                                    name="title"
                                    required
                                    autoComplete="off"
                                  />
                                </div>

                                <small className="form-text text-muted">
                                  Enter the name of the course or class.
                                </small>
                              </div>

                              {/* Description */}

                              <div className="col-lg-6">
                                <label className="form-label">
                                  Short Description
                                </label>

                                <div className="course-textarea-wrapper">
                                  <i className="ri-file-text-line"></i>

                                  <textarea
                                    className="form-control"
                                    placeholder="Enter a short description..."
                                    name="description"
                                    rows="3"
                                  />
                                </div>

                                <small className="form-text text-muted">
                                  Optional description for this course.
                                </small>
                              </div>
                            </div>
                          </div>

                          {/* =================================================
                              SUBJECT SELECTION
                          ================================================= */}

                          <div className="form-section mt-4">
                            <div className="form-section-heading">
                              <div className="form-section-number">2</div>

                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                                  <div>
                                    <h6 className="mb-0">
                                      Select Subjects
                                      <span className="text-danger ms-1">
                                        *
                                      </span>
                                    </h6>

                                    <small className="text-muted">
                                      Choose the subjects that belong to this
                                      course.
                                    </small>
                                  </div>

                                  <div className="subject-selected-count">
                                    <i className="ri-checkbox-circle-line me-1"></i>

                                    {selectedSubjects.length}

                                    {" / "}

                                    {subjects.length}

                                    {" selected"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Subject Toolbar */}

                            <div className="subject-toolbar">
                              <div className="subject-search">
                                <i className="ri-search-line"></i>

                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search subjects..."
                                  value={subjectSearch}
                                  onChange={(e) =>
                                    setSubjectSearch(e.target.value)
                                  }
                                />

                                {subjectSearch && (
                                  <button
                                    type="button"
                                    className="subject-search-clear"
                                    onClick={() => setSubjectSearch("")}
                                  >
                                    <i className="ri-close-line"></i>
                                  </button>
                                )}
                              </div>

                              <div className="subject-actions">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={selectAllSubjects}
                                  disabled={!subjects.length}
                                >
                                  <i className="ri-checkbox-multiple-line me-1"></i>
                                  Select All
                                </button>

                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={clearAllSubjects}
                                  disabled={!selectedSubjects.length}
                                >
                                  <i className="ri-checkbox-blank-line me-1"></i>
                                  Clear All
                                </button>
                              </div>
                            </div>

                            {/* Subject Cards */}

                            {filteredSubjects.length > 0 ? (
                              <div className="subject-grid">
                                {filteredSubjects.map((subject, index) => {
                                  const isSelected = selectedSubjects.includes(
                                    subject.id,
                                  );

                                  return (
                                    <label
                                      key={subject.id ?? index}
                                      className={`subject-card ${
                                        isSelected ? "selected" : ""
                                      }`}
                                      htmlFor={`subject-${subject.id}`}
                                    >
                                      <input
                                        type="checkbox"
                                        id={`subject-${subject.id}`}
                                        name="subjects[]"
                                        value={subject.id}
                                        checked={isSelected}
                                        onChange={() =>
                                          toggleSubject(subject.id)
                                        }
                                      />

                                      <div className="subject-check">
                                        {isSelected && (
                                          <i className="ri-check-line"></i>
                                        )}
                                      </div>

                                      <div className="subject-card-content">
                                        <span className="subject-card-name">
                                          {subject?.name || "Unnamed Subject"}
                                        </span>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="subject-empty">
                                <div className="subject-empty-icon">
                                  <i className="ri-search-line"></i>
                                </div>

                                <strong>No subjects found</strong>

                                <small>Try another search term.</small>
                              </div>
                            )}

                            {/* Selected Subjects Summary */}

                            {selectedSubjects.length > 0 && (
                              <div className="selected-subject-summary">
                                <div className="selected-summary-header">
                                  <div>
                                    <i className="ri-list-check-2 me-1"></i>
                                    Selected Subjects
                                  </div>

                                  <span>{selectedSubjects.length}</span>
                                </div>

                                <div className="selected-summary-list">
                                  {subjects
                                    .filter((subject) =>
                                      selectedSubjects.includes(subject.id),
                                    )
                                    .map((subject) => (
                                      <span
                                        key={subject.id}
                                        className="selected-subject-chip"
                                      >
                                        {subject.name}

                                        <button
                                          type="button"
                                          onClick={() =>
                                            toggleSubject(subject.id)
                                          }
                                        >
                                          <i className="ri-close-line"></i>
                                        </button>
                                      </span>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* =================================================
                            FOOTER
                        ================================================= */}

                        <div className="course-form-footer">
                          <div className="text-muted small">
                            <i className="ri-information-line me-1"></i>
                            Fields marked with{" "}
                            <span className="text-danger">*</span> are required.
                          </div>

                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => navigate("/courses")}
                              disabled={loading}
                            >
                              Cancel
                            </button>

                            <button
                              type="submit"
                              className="btn btn-primary px-4"
                              disabled={loading || !selectedSubjects.length}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Creating...
                                </>
                              ) : (
                                <>
                                  <i className="ri-check-line me-1"></i>
                                  Create Course
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          STYLES
      ========================================================= */}

      <style>{`

        /* =====================================================
           MAIN CARD
        ===================================================== */

        .course-add-card {
          overflow: hidden;

          border: 1px solid
            rgba(127, 127, 127, 0.14);
        }


        /* =====================================================
           HEADER
        ===================================================== */

        .course-add-header {
          padding: 20px 22px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;

          flex-wrap: wrap;

          border-bottom: 1px solid
            rgba(127, 127, 127, 0.14);
        }

        .course-add-icon {
          width: 46px;
          height: 46px;

          min-width: 46px;

          border-radius: 11px;

          display: flex;
          align-items: center;
          justify-content: center;

          background: rgba(
            var(--bs-primary-rgb),
            0.10
          );

          color: var(--bs-primary);

          font-size: 23px;
        }

        .course-add-header h4 {
          font-size: 19px;
        }

        .course-add-header p {
          font-size: 12px;
        }


        /* =====================================================
           FORM BODY
        ===================================================== */

        .course-form-body {
          padding: 24px 22px;
        }


        /* =====================================================
           FORM SECTION
        ===================================================== */

        .form-section {
          padding: 18px;

          border: 1px solid
            rgba(127, 127, 127, 0.14);

          border-radius: 11px;
        }

        .form-section-heading {
          display: flex;

          align-items: center;

          gap: 11px;
        }

        .form-section-number {
          width: 30px;
          height: 30px;

          min-width: 30px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 8px;

          background: rgba(
            var(--bs-primary-rgb),
            0.10
          );

          color: var(--bs-primary);

          font-size: 12px;

          font-weight: 700;
        }

        .form-section-heading h6 {
          font-size: 14px;
          font-weight: 600;
        }

        .form-section-heading small {
          display: block;

          margin-top: 2px;

          font-size: 11px;
        }


        /* =====================================================
           FORM INPUTS
        ===================================================== */

        .form-section .form-label {
          font-size: 12px;

          font-weight: 600;

          margin-bottom: 7px;
        }

        .course-input-wrapper,
        .course-textarea-wrapper {
          position: relative;
        }

        .course-input-wrapper > i,
        .course-textarea-wrapper > i {
          position: absolute;

          left: 12px;
          top: 13px;

          font-size: 17px;

          opacity: 0.50;

          z-index: 2;

          pointer-events: none;
        }

        .course-input-wrapper .form-control {
          height: 44px;

          padding-left: 38px;

          border-radius: 8px;
        }

        .course-textarea-wrapper .form-control {
          min-height: 88px;

          padding-left: 38px;
          padding-top: 11px;

          resize: vertical;

          border-radius: 8px;
        }

        .form-text {
          font-size: 10px;

          margin-top: 5px;
        }


        /* =====================================================
           SUBJECT TOOLBAR
        ===================================================== */

        .subject-toolbar {
          margin-top: 20px;

          padding: 12px;

          border-radius: 9px;

          background: rgba(
            127,
            127,
            127,
            0.045
          );

          border: 1px solid
            rgba(127, 127, 127, 0.12);

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 10px;

          flex-wrap: wrap;
        }

        .subject-search {
          position: relative;

          width: 300px;
        }

        .subject-search > i {
          position: absolute;

          left: 11px;
          top: 50%;

          transform: translateY(-50%);

          font-size: 16px;

          opacity: 0.50;

          pointer-events: none;

          z-index: 2;
        }

        .subject-search .form-control {
          height: 38px;

          padding-left: 35px;
          padding-right: 35px;

          border-radius: 8px;

          font-size: 12px;
        }

        .subject-search-clear {
          position: absolute;

          right: 6px;
          top: 50%;

          transform: translateY(-50%);

          width: 26px;
          height: 26px;

          border: 0;

          border-radius: 6px;

          background: transparent;

          color: inherit;

          display: flex;

          align-items: center;
          justify-content: center;

          opacity: 0.55;

          cursor: pointer;
        }

        .subject-search-clear:hover {
          background: rgba(
            127,
            127,
            127,
            0.08
          );

          opacity: 1;
        }

        .subject-actions {
          display: flex;

          align-items: center;

          gap: 6px;
        }

        .subject-actions .btn {
          font-size: 11px;
        }


        /* =====================================================
           SELECTED COUNT
        ===================================================== */

        .subject-selected-count {
          padding: 6px 9px;

          border-radius: 7px;

          background: rgba(
            var(--bs-primary-rgb),
            0.08
          );

          color: var(--bs-primary);

          font-size: 11px;

          font-weight: 600;

          white-space: nowrap;
        }


        /* =====================================================
           SUBJECT GRID
        ===================================================== */

        .subject-grid {
          margin-top: 14px;

          display: grid;

          grid-template-columns:
            repeat(
              4,
              minmax(0, 1fr)
            );

          gap: 9px;

          max-height: 360px;

          overflow-y: auto;

          padding: 2px;
        }

        .subject-grid::-webkit-scrollbar {
          width: 5px;
        }

        .subject-grid::-webkit-scrollbar-track {
          background: transparent;
        }

        .subject-grid::-webkit-scrollbar-thumb {
          background: rgba(
            127,
            127,
            127,
            0.25
          );

          border-radius: 10px;
        }


        /* =====================================================
           SUBJECT CARD
        ===================================================== */

        .subject-card {
          min-height: 52px;

          padding: 9px 10px;

          border: 1px solid
            rgba(127, 127, 127, 0.18);

          border-radius: 8px;

          display: flex;

          align-items: center;

          gap: 9px;

          cursor: pointer;

          transition:
            background 0.15s ease,
            border-color 0.15s ease,
            transform 0.15s ease;
        }

        .subject-card:hover {
          background: rgba(
            127,
            127,
            127,
            0.05
          );

          border-color: rgba(
            127,
            127,
            127,
            0.30
          );
        }

        .subject-card.selected {
          border-color: rgba(
            var(--bs-primary-rgb),
            0.40
          );

          background: rgba(
            var(--bs-primary-rgb),
            0.055
          );
        }

        .subject-card input {
          display: none;
        }

        .subject-check {
          width: 20px;
          height: 20px;

          min-width: 20px;

          border: 1.5px solid
            rgba(127, 127, 127, 0.35);

          border-radius: 5px;

          display: flex;

          align-items: center;
          justify-content: center;

          font-size: 13px;

          transition: all 0.15s ease;
        }

        .subject-card.selected
          .subject-check {
          background: var(--bs-primary);

          border-color: var(--bs-primary);

          color: #fff;
        }

        .subject-card-content {
          min-width: 0;
        }

        .subject-card-name {
          display: block;

          font-size: 12px;

          font-weight: 500;

          line-height: 1.35;

          overflow: hidden;

          text-overflow: ellipsis;

          white-space: nowrap;
        }


        /* =====================================================
           SELECTED SUBJECT SUMMARY
        ===================================================== */

        .selected-subject-summary {
          margin-top: 14px;

          padding: 11px;

          border-radius: 8px;

          background: rgba(
            var(--bs-primary-rgb),
            0.035
          );

          border: 1px solid rgba(
            var(--bs-primary-rgb),
            0.14
          );
        }

        .selected-summary-header {
          display: flex;

          align-items: center;

          justify-content: space-between;

          margin-bottom: 8px;

          font-size: 11px;

          font-weight: 600;
        }

        .selected-summary-header > span {
          min-width: 22px;
          height: 22px;

          display: flex;

          align-items: center;
          justify-content: center;

          border-radius: 6px;

          background: rgba(
            var(--bs-primary-rgb),
            0.10
          );

          color: var(--bs-primary);

          font-size: 10px;
        }

        .selected-summary-list {
          display: flex;

          align-items: center;

          flex-wrap: wrap;

          gap: 5px;
        }

        .selected-subject-chip {
          display: inline-flex;

          align-items: center;

          gap: 4px;

          padding: 5px 7px 5px 9px;

          border-radius: 6px;

          background: rgba(
            127,
            127,
            127,
            0.08
          );

          border: 1px solid
            rgba(127, 127, 127, 0.12);

          font-size: 11px;
        }

        .selected-subject-chip button {
          width: 18px;
          height: 18px;

          padding: 0;

          border: 0;

          border-radius: 4px;

          display: flex;

          align-items: center;
          justify-content: center;

          background: transparent;

          color: inherit;

          opacity: 0.55;

          cursor: pointer;
        }

        .selected-subject-chip button:hover {
          opacity: 1;

          background: rgba(
            127,
            127,
            127,
            0.10
          );
        }


        /* =====================================================
           EMPTY SUBJECT STATE
        ===================================================== */

        .subject-empty {
          min-height: 150px;

          margin-top: 14px;

          display: flex;

          flex-direction: column;

          align-items: center;
          justify-content: center;

          border: 1px dashed
            rgba(127, 127, 127, 0.25);

          border-radius: 9px;

          text-align: center;
        }

        .subject-empty-icon {
          width: 42px;
          height: 42px;

          margin-bottom: 8px;

          display: flex;

          align-items: center;
          justify-content: center;

          border-radius: 9px;

          background: rgba(
            var(--bs-primary-rgb),
            0.08
          );

          color: var(--bs-primary);

          font-size: 19px;
        }

        .subject-empty strong {
          font-size: 12px;
        }

        .subject-empty small {
          margin-top: 3px;

          font-size: 10px;

          opacity: 0.60;
        }


        /* =====================================================
           FORM FOOTER
        ===================================================== */

        .course-form-footer {
          padding: 15px 22px;

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 15px;

          flex-wrap: wrap;

          border-top: 1px solid
            rgba(127, 127, 127, 0.14);
        }

        .course-form-footer .btn {
          min-height: 38px;

          border-radius: 8px;
        }


        /* =====================================================
           RESPONSIVE
        ===================================================== */

        @media (max-width: 1199px) {

          .subject-grid {
            grid-template-columns:
              repeat(
                3,
                minmax(0, 1fr)
              );
          }

        }


        @media (max-width: 991px) {

          .subject-grid {
            grid-template-columns:
              repeat(
                2,
                minmax(0, 1fr)
              );
          }

        }


        @media (max-width: 767px) {

          .course-add-header {
            align-items: stretch;
          }

          .course-add-header
            > .btn {
            width: 100%;
          }

          .subject-toolbar {
            align-items: stretch;
          }

          .subject-search {
            width: 100%;
          }

          .subject-actions {
            width: 100%;
          }

          .subject-actions .btn {
            flex: 1;
          }

          .course-form-footer {
            align-items: stretch;
            flex-direction: column;
          }

          .course-form-footer
            > .d-flex {
            width: 100%;
          }

          .course-form-footer
            > .d-flex .btn {
            flex: 1;
          }

        }


        @media (max-width: 575px) {

          .course-add-header,
          .course-form-body,
          .course-form-footer {
            padding-left: 15px;
            padding-right: 15px;
          }

          .form-section {
            padding: 14px;
          }

          .subject-grid {
            grid-template-columns: 1fr;
          }

          .course-add-header h4 {
            font-size: 17px;
          }

          .course-add-icon {
            width: 42px;
            height: 42px;
            min-width: 42px;
          }

        }

      `}</style>
    </>
  );
}
